import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  MdKingBed, MdAccountBalanceWallet, MdList, MdGridView,
  MdMedicalServices, MdPrint, MdLogout, MdAdd,
  MdWarning, MdAccessTime, MdLocalHospital, MdSearch, MdPeople,
} from 'react-icons/md';
import { PageShell, Badge, DataTable, StatCard } from '../../components/Ui';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';
import { printDischargeSummary } from '../../utils/hospitalPdf';
import { canAccess, canDischargePatient } from '../../permissions';
import { useAuth } from '../../auth/AuthContext';
import { opVisitsForPatient } from '../../utils/opIpLink';

function mapAdmission(a) {
  return {
    ipid: a.admissionId,
    name: a.patientName,
    bed: a.bedLabel || a.bedId,
    ward: a.ward || '—',
    pending: a.pendingBill || 0,
    status: a.status === 'admitted' ? 'Under Care' : a.status,
    doctor: a.doctor,
    admissionId: a.admissionId,
    admittedAt: a.admittedAt,
    insurance: a.insurance,
    _raw: a,
  };
}

function daysAdmitted(admittedAt) {
  if (!admittedAt) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(admittedAt).getTime()) / 86400000));
}

function PatientCard({ row, onPrint, onDischarge, busy, showDischarge }) {
  const days = daysAdmitted(row.admittedAt);
  const highBill = row.pending > 50000;
  const advancePaid = Number(row._raw?.advanceAmount || 0);
  const balanceDue = Math.max(0, Number(row.pending) - advancePaid);
  const isLongStay = days > 7;
  const isEmergency = (row._raw?.admitType || '').toLowerCase().includes('emergency');
  const isTPA = row.insurance && row.insurance !== 'Cash';

  const borderColor = isEmergency ? '#ef4444' : isTPA ? '#6366f1' : isLongStay ? '#f59e0b' : highBill ? '#dc2626' : '#0d6e6a';

  return (
    <div className="ipdlist-card" style={{ borderLeft: `4px solid ${borderColor}` }}>
      <div className="ipdlist-card-top">
        <div className="ipdlist-card-avatar" style={{ background: row.status === 'Discharged' ? '#94a3b8' : borderColor }}>
          {(row.name || 'P').charAt(0)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ipdlist-card-name" style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
            {row.name}
            {row._raw?.age && <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b' }}>{row._raw.age}y / {row._raw.gender || '—'}</span>}
            {isEmergency && <span style={{ fontSize:10, fontWeight:800, background:'#fef2f2', color:'#dc2626', border:'1px solid #fca5a5', borderRadius:5, padding:'1px 6px' }}>🚨 EMRG</span>}
            {isTPA && <span style={{ fontSize:10, fontWeight:800, background:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe', borderRadius:5, padding:'1px 6px' }}>🛡 TPA</span>}
            {isLongStay && <span style={{ fontSize:10, fontWeight:800, background:'#fffbeb', color:'#b45309', border:'1px solid #fcd34d', borderRadius:5, padding:'1px 6px' }}>⏰ {days}d STAY</span>}
          </div>
          <div className="ipdlist-card-meta">
            <span><MdKingBed style={{ verticalAlign: '-2px', fontSize: 13 }} /> {row.ward} · Bed {row.bed}</span>
            <span><MdMedicalServices style={{ verticalAlign: '-2px', fontSize: 13 }} /> Dr. {row.doctor || '—'}</span>
          </div>
          <div className="ipdlist-card-meta">
            <span style={{ color: '#64748b', fontFamily: 'monospace', fontSize: 11.5 }}>IP ID: {row.ipid}</span>
            {row.insurance && <span> · {isTPA ? `🛡 ${row.insurance}` : '💵 Cash'}</span>}
            {row._raw?.admitType && <span style={{ color: '#8b5cf6' }}> · {row._raw.admitType}</span>}
          </div>
          {row._raw?.reason && (
            <div style={{ fontSize: 11.5, color: '#64748b', fontStyle: 'italic', marginTop: 2 }}>
              Dx: {row._raw.reason.slice(0, 70)}{row._raw.reason.length > 70 ? '…' : ''}
            </div>
          )}
        </div>
        <Badge tone={row.status === 'Discharged' ? 'default' : 'success'}>{row.status}</Badge>
      </div>

      {/* Financial summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, margin: '8px 0', padding: '8px 10px', background: '#f8fafc', borderRadius: 8 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Total Bill</div>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 14, fontWeight: 800, color: highBill ? '#dc2626' : '#0f172a' }}>₹{Number(row.pending).toLocaleString('en-IN')}</div>
        </div>
        <div style={{ textAlign: 'center', borderLeft: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Advance</div>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 14, fontWeight: 800, color: '#10b981' }}>₹{advancePaid.toLocaleString('en-IN')}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Balance</div>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 14, fontWeight: 800, color: balanceDue > 0 ? '#ef4444' : '#10b981' }}>
            {balanceDue > 0 ? `₹${balanceDue.toLocaleString('en-IN')}` : '✓ Paid'}
          </div>
        </div>
      </div>

      <div className="ipdlist-card-bottom">
        <div className="ipdlist-card-kpis">
          <div className="ipdlist-card-kpi">
            <MdAccessTime style={{ fontSize: 14, color: '#64748b' }} />
            <span>{days}d stay · Admitted {row._raw?.admittedAt ? new Date(row._raw.admittedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}</span>
          </div>
          {highBill && (
            <div className="ipdlist-card-kpi" style={{ color: '#dc2626' }}>
              <MdWarning style={{ fontSize: 14 }} />
              <span>High bill alert</span>
            </div>
          )}
        </div>
        <div className="row-actions">
          <Link to={`/ipd/patient/${row.admissionId}`} className="btn btn-sm btn-primary">📋 Open Chart</Link>
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => onPrint(row)}>
            <MdPrint /> Bill
          </button>
          {showDischarge && row.status !== 'Discharged' && (
            <button type="button" className="btn btn-sm btn-secondary" disabled={busy === row.admissionId} onClick={() => onDischarge(row)}>
              <MdLogout /> Discharge
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


export default function IpdList() {
  const { data, loading, error, reload } = useLiveData(() => ipApi.getAdmissions(), []);
  const { user } = useAuth();
  const { hospital } = useHospitalProfile();
  const rows = (data?.admissions || []).map(mapAdmission);
  const outstanding = data?.totalOutstanding || 0;
  const [busy, setBusy] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [filter, setFilter] = useState('active');
  const [search, setSearch] = useState('');
  const [wardFilter, setWardFilter] = useState('all');
  const [doctorFilter, setDoctorFilter] = useState('all');

  /* All distinct wards and doctors */
  const wards = useMemo(() => {
    return [...new Set(rows.map((r) => r.ward).filter(Boolean))];
  }, [rows]);
  const doctors = useMemo(() => {
    return [...new Set(rows.map((r) => r.doctor).filter(Boolean))];
  }, [rows]);

  const filtered = useMemo(() => {
    let list = rows;
    // Status filter
    if (filter === 'active') list = list.filter((r) => r.status !== 'Discharged' && r.status !== 'discharged');
    else if (filter === 'discharged') list = list.filter((r) => r.status === 'Discharged' || r.status === 'discharged');
    else if (filter === 'emergency') list = list.filter((r) => (r._raw?.admitType || '').toLowerCase().includes('emergency'));
    else if (filter === 'insurance') list = list.filter((r) => r.insurance && r.insurance !== 'Cash');
    else if (filter === 'longstay') list = list.filter((r) => daysAdmitted(r.admittedAt) > 7);
    else if (filter === 'highbill') list = list.filter((r) => r.pending > 50000);
    // Ward filter
    if (wardFilter !== 'all') list = list.filter((r) => r.ward === wardFilter);
    // Doctor filter
    if (doctorFilter !== 'all') list = list.filter((r) => r.doctor === doctorFilter);
    // Search — name, IP ID, bed, doctor, diagnosis
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.ipid || '').toLowerCase().includes(q) ||
        (r.doctor || '').toLowerCase().includes(q) ||
        (r.bed || '').toLowerCase().includes(q) ||
        (r._raw?.reason || '').toLowerCase().includes(q) ||
        (r._raw?.diagnosis || '').toLowerCase().includes(q) ||
        (r.insurance || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [rows, filter, wardFilter, doctorFilter, search]);

  const activeRows = rows.filter((r) => r.status !== 'Discharged');
  const highBillRows = rows.filter((r) => r.pending > 50000);
  const longStay = rows.filter((r) => daysAdmitted(r.admittedAt) > 7);
  const emergencyRows = rows.filter((r) => (r._raw?.admitType || '').toLowerCase().includes('emergency'));
  const tpaRows = rows.filter((r) => r.insurance && r.insurance !== 'Cash');

  const printBill = (row) => {
    const adm = row._raw || row;
    printDischargeSummary({ ...adm, pendingBill: adm.pendingBill ?? row.pending }, hospital, user);
  };

  const discharge = async (row) => {
    const admissionId = row.admissionId || row.ipid;
    if (!window.confirm('Discharge this patient? Final bill PDF will open.')) return;
    setBusy(admissionId);
    try {
      const res = await ipApi.discharge(admissionId);
      const adm = res?.admission || row._raw || row;
      printDischargeSummary(adm, res?.hospital || hospital, user);
      await reload();
    } finally { setBusy(''); }
  };

  const showDischarge = canDischargePatient(user?.role);

  const columns = [
    { key: 'ipid', label: 'IP ID', render: (r) => <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{r.ipid}</span> },
    {
      key: 'name', label: 'Patient',
      render: (r) => (
        <div className="cell-stack">
          <span className="cell-strong">{r.name}</span>
          <span className="cell-sub">{r.insurance ? `🛡 ${r.insurance}` : 'Cash'}</span>
        </div>
      ),
    },
    {
      key: 'bed', label: 'Ward / Bed',
      render: (r) => (
        <div className="cell-stack">
          <span className="cell-strong">{r.ward}</span>
          <span className="cell-sub">Bed: {r.bed}</span>
        </div>
      ),
    },
    { key: 'doctor', label: 'Doctor' },
    {
      key: 'admittedAt', label: 'Stay',
      render: (r) => {
        const d = daysAdmitted(r.admittedAt);
        return <span style={{ color: d > 7 ? '#ef4444' : '#0f172a' }}>{d}d</span>;
      },
    },
    {
      key: 'pending', label: 'Running Bill',
      render: (r) => (
        <span style={{ fontWeight: 700, color: r.pending > 50000 ? '#dc2626' : '#0f172a' }}>
          ₹{Number(r.pending).toLocaleString('en-IN')}
        </span>
      ),
    },
    { key: 'status', label: 'Status', render: (r) => <Badge tone={r.status === 'Discharged' ? 'default' : 'warning'}>{r.status}</Badge> },
    {
      key: 'actions', label: 'Actions',
      render: (r) => (
        <div className="row-actions">
          <Link to={`/ipd/patient/${r.admissionId}`} className="btn btn-sm btn-primary">Chart</Link>
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => printBill(r)}>Bill PDF</button>
          {showDischarge && r.status !== 'Discharged' && (
            <button type="button" className="btn btn-sm btn-secondary" disabled={busy === r.admissionId} onClick={() => discharge(r)}>
              Discharge
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <PageShell
      title="IPD — Inpatient Management"
      subtitle="All admissions · Bills · Discharge"
      actions={
        <>
          {canAccess(user?.role, '/ipd/admit') && <Link to="/ipd/admit" className="btn btn-primary"><MdAdd /> Admit Patient</Link>}
          <Link to="/ipd/beds" className="btn btn-secondary">Bed Map</Link>
        </>
      }
    >
      {error && <div className="error mb">{error}</div>}

      {/* Stats — 6 cards */}
      <div className="stat-grid">
        <StatCard label="Active Admissions" value={activeRows.length} accent="#0d6e6a" icon={MdKingBed} loading={loading} />
        <StatCard label="Total Outstanding" value={`₹${outstanding.toLocaleString('en-IN')}`} accent="#f97316" icon={MdAccountBalanceWallet} loading={loading} />
        <StatCard label="Emergency Cases" value={emergencyRows.length} accent="#ef4444" icon={MdLocalHospital} loading={loading} />
        <StatCard label="Insurance / TPA" value={tpaRows.length} accent="#6366f1" icon={MdPeople} loading={loading} />
        <StatCard label="High Bill (>₹50k)" value={highBillRows.length} accent="#dc2626" icon={MdWarning} loading={loading} />
        <StatCard label="Long Stay (>7d)" value={longStay.length} accent="#f59e0b" icon={MdAccessTime} loading={loading} />
      </div>

      {/* Smart filter tabs + search + ward + doctor filters */}
      <div className="ipdlist-toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 160 }}>
          <MdSearch style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#94a3b8', fontSize:18 }} />
          <input
            className="input"
            style={{ paddingLeft: 34 }}
            placeholder="Search name, IP ID, bed, diagnosis, doctor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {/* Filter tabs */}
        <div className="row-actions" style={{ gap: 4, flexWrap: 'wrap' }}>
          {[
            { id: 'all', label: `All (${rows.length})` },
            { id: 'active', label: `Active (${activeRows.length})` },
            { id: 'emergency', label: `🚨 Emergency (${emergencyRows.length})` },
            { id: 'insurance', label: `🛡 TPA (${tpaRows.length})` },
            { id: 'longstay', label: `⏰ Long Stay (${longStay.length})` },
            { id: 'highbill', label: `⚠ High Bill (${highBillRows.length})` },
            { id: 'discharged', label: `Discharged (${rows.length - activeRows.length})` },
          ].map((f) => (
            <button key={f.id} type="button" className={`emr-chip ${filter === f.id ? 'active' : ''}`} onClick={() => setFilter(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
        {/* Ward filter */}
        {wards.length > 0 && (
          <select className="input input-sm" style={{ width: 140 }} value={wardFilter} onChange={(e) => setWardFilter(e.target.value)}>
            <option value="all">All wards</option>
            {wards.map((w) => <option key={w}>{w}</option>)}
          </select>
        )}
        {/* Doctor filter */}
        {doctors.length > 0 && (
          <select className="input input-sm" style={{ width: 160 }} value={doctorFilter} onChange={(e) => setDoctorFilter(e.target.value)}>
            <option value="all">All doctors</option>
            {doctors.map((d) => <option key={d} value={d}>Dr. {d}</option>)}
          </select>
        )}
        {/* View toggle */}
        <div className="row-actions" style={{ gap: 4, marginLeft: 'auto' }}>
          <button type="button" className={`emr-chip ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')}>
            <MdGridView style={{ verticalAlign: '-2px' }} /> Cards
          </button>
          <button type="button" className={`emr-chip ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')}>
            <MdList style={{ verticalAlign: '-2px' }} /> Table
          </button>
        </div>
      </div>
      {/* Results count */}
      <p style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 12px', fontWeight: 600 }}>
        Showing {filtered.length} of {rows.length} patients
        {search && ` · Search: "${search}"`}
      </p>

      {/* Content */}
      {viewMode === 'cards' ? (
        <div className="ipdlist-card-grid">
          {loading && <p className="hint table-empty">Loading…</p>}
          {!loading && filtered.length === 0 && <p className="hint table-empty">No patients match your filter.</p>}
          {filtered.map((r) => (
            <PatientCard key={r.ipid} row={r} onPrint={printBill} onDischarge={discharge} busy={busy} showDischarge={showDischarge} />
          ))}
        </div>
      ) : (
        <DataTable columns={columns} rows={filtered} keyField="ipid" loading={loading} emptyMessage='No admitted patients. Use "Admit Patient" to admit.' />
      )}
    </PageShell>
  );
}
